import { useState, useEffect, useCallback } from 'react';
import { DataGrid, DataGridSkeleton } from '@cw-ui/data-grid';
import Button from '@mui/material/Button';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import Columns from '../columns/columns';
import Locale from 'src/i18n';
import { setIsTemplateDownloadFailed, setIsTemplateDownloadSuccess } from '../../slice';
import { formatMessage } from 'src/i18n/intl';
import {
    useLazyGetSampleFileQuery,
    useLazyGetUploadedFilesQuery,
    useGetUploadedUsersQuery,
    useEditUserRowMutation,
    useCreateUsersFromUploadMutation,
} from '../../services/bulkUserImportService';
import { downloadToCsv } from '../../utils/util';
import {
    bulkImportUsersSliceId,
    setIsFetchUsersFailed,
    setUsers,
    setOverallTotalItems,
    setIsImportSuccess,
    setIsImportError,
    clearImportError,
    setIsUpdateUserFailed,
    setCurrentFileName,
    setCurrentFileId,
} from '../../slice';
import { useDataGridSearchAndFilter } from '../../hooks/useDataGridSearchAndFilter';
import { DATA_GRID_DEFAULTS } from '../../constants';
import * as Styled from './BulkUserImportGrid.styles';
import { ImportAllUsersModal, BULK_IMPORT_OPTIONS } from '../ImportAllUsersModal/ImportAllUsersModal';
import ToastNotification from 'src/asio-neon-components/ToastNotification';
import NoRowsOverlay from '../NoRowsOverlay/NoRowsOverlay';

export const BulkUserImportGrid = () => {
    const dispatch = useDispatch();
    const [triggerGetUploadFiles, { isFetching: isFilesLoading }] = useLazyGetUploadedFilesQuery();
    const [triggerGetSample, { isFetching }] = useLazyGetSampleFileQuery();
    const [editUserRow, { isLoading: isUpdatingUsers }] = useEditUserRowMutation();
    const [createUsersFromUpload, { isLoading: isCreatingUsers }] = useCreateUsersFromUploadMutation();

    // Get uploadId from Redux
    const uploadId = useSelector(state => state[bulkImportUsersSliceId]?.currentUploadId);
    const isUploadingFiles = useSelector(state => state[bulkImportUsersSliceId]?.isUploadingFiles);
    const isRemoveFileLoading = useSelector(state => state[bulkImportUsersSliceId]?.isRemoveFileLoading);
    const isDeletingFile = useSelector(state => state[bulkImportUsersSliceId]?.isDeletingFile);
    const bulkUsers = useSelector(state => state[bulkImportUsersSliceId]?.users.data);
    const totalCount = useSelector(state => state[bulkImportUsersSliceId]?.users.totalItems);
    const overallTotalItems = useSelector(state => state[bulkImportUsersSliceId]?.overallTotalItems);
    const isImportSuccess = useSelector(state => state[bulkImportUsersSliceId]?.isImportSuccess);
    const importSuccessMessage = useSelector(state => state[bulkImportUsersSliceId]?.importSuccessMessage);
    const isImportError = useSelector(state => state[bulkImportUsersSliceId]?.isImportError);
    const importErrorMessage = useSelector(state => state[bulkImportUsersSliceId]?.importErrorMessage);
    const isRemoveUserLoading = useSelector(state => state[bulkImportUsersSliceId]?.isRemoveUserLoading);
    const [rows, setRows] = useState(bulkUsers);
    const [selectedRows, setSelectedRows] = useState([]);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [showImportAllUsersModal, setShowImportAllUsersModal] = useState(false);
    const [rowModesModel, setRowModesModel] = useState({});
    const [currentQueryParams, setCurrentQueryParams] = useState(null);
    const [density, setDensity] = useState('standard');

    // Use query
    const { data: usersData, isFetching: isUsersLoading } = useGetUploadedUsersQuery(
        currentQueryParams || { uploadId, page: 1, pageSize: 25, search: '', filter: '' },
        { skip: !currentQueryParams && !uploadId }
    );

    const isGridLoading =
        isUsersLoading ||
        isFilesLoading ||
        isFetching ||
        isCreatingUsers ||
        isUpdatingUsers ||
        isUploadingFiles ||
        isRemoveFileLoading ||
        isDeletingFile ||
        isRemoveUserLoading;

    // Update Redux when usersData changes
    useEffect(() => {
        if (usersData) {
            dispatch(setUsers(usersData));
            const { search = '', filter = '' } = currentQueryParams || {};
            const isUnfiltered = !search.trim() && !filter.trim();
            if (isUnfiltered) {
                dispatch(setOverallTotalItems(usersData.totalItems || 0));
            }
        }
    }, [usersData, dispatch, currentQueryParams]);

    // Update rows when bulkUsers changes
    useEffect(() => {
        setRows(bulkUsers);
    }, [bulkUsers]);

    useEffect(() => {
        const initializeUploadId = async () => {
            try {
                const result = await triggerGetUploadFiles().unwrap();
                if (result && result.length > 0) {
                    const foundUploadId = result[0].id;
                    dispatch(setCurrentFileName(result[0].source || ''));
                    dispatch(setCurrentFileId(foundUploadId));
                    // This will trigger the useGetUploadedUsersQuery due to uploadId change
                }
            } catch (error) {
                console.error('Failed to fetch upload files:', error);
                dispatch(setIsFetchUsersFailed());
            }
        };

        initializeUploadId();
    }, [dispatch, triggerGetUploadFiles]); // Run only once on mount

    // Fetch users using a specific upload ID
    const fetchUsersWithUploadId = useCallback((uploadIdToUse, page = 1, pageSize = 25, search = '', filter = '') => {
        if (uploadIdToUse) {
            const queryParams = {
                uploadId: uploadIdToUse,
                page,
                pageSize,
                search,
                filter,
            };
            setCurrentQueryParams(queryParams);
        }
    }, []);

    // Data fetch function for the search and filter hook
    const handleDataFetch = useCallback(
        ({ search, filter, resetToFirstPage }) => {
            if (uploadId) {
                const page = resetToFirstPage ? 1 : paginationModel.page + 1;
                const pageSize = paginationModel.pageSize;
                fetchUsersWithUploadId(uploadId, page, pageSize, search, filter);
            }
        },
        [uploadId, paginationModel.page, paginationModel.pageSize, fetchUsersWithUploadId]
    );

    const resetPagination = () => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const { searchProps, filterProps, getCurrentSearchAndFilter } = useDataGridSearchAndFilter({
        onDataFetch: handleDataFetch,
        debounceDelay: 1000,
        resetPagination,
    });

    const downloadSampleFile = async () => {
        try {
            const result = await triggerGetSample().unwrap();
            downloadToCsv(result);
            dispatch(setIsTemplateDownloadSuccess());
        } catch (error) {
            dispatch(setIsTemplateDownloadFailed());
        }
    };

    const handleImportAllUsersClick = () => {
        setShowImportAllUsersModal(true);
    };

    const handleImportAllUsersModalClose = () => {
        setShowImportAllUsersModal(false);
    };

    const handleImportSuccess = () => {
        dispatch(setIsImportSuccess(''));
    };

    const handleImportError = () => {
        dispatch(clearImportError());
    };

    const handleImportAllUsersModalContinue = async (option, selectedUserImport = false) => {
        setShowImportAllUsersModal(false);

        if (!uploadId) {
            console.error('No upload ID available for import');
            return;
        }

        try {
            let requestPayload = { uploadId };
            let successMessage = '';
            if (selectedUserImport) {
                requestPayload.users = selectedRows;
                successMessage = formatMessage(Locale.selectedUsersImportedSuccessfully);
            } else {
                if (option === BULK_IMPORT_OPTIONS.VISIBLE_ON_PAGE) {
                    requestPayload.users = (rows || []).map(user => user.id).filter(id => id != null && id !== '');
                    successMessage = formatMessage(Locale.usersImportedSuccessfully);
                } else {
                    successMessage = formatMessage(Locale.allUsersImportedSuccessfully);
                }
            }

            await createUsersFromUpload(requestPayload).unwrap();
            dispatch(setIsImportSuccess(successMessage));
        } catch (error) {
            dispatch(setIsImportError(formatMessage(Locale.failedImportUsers)));
        }
    };

    const handleRowModesModelChange = useCallback(
        newRowModesModel => {
            setRowModesModel(newRowModesModel);
        },
        [setRowModesModel]
    );

    const hasActiveFilters =
        filterProps.filterModel.items.length > 0 || filterProps.filterModel.quickFilterValues.length > 0;

    const columnsForGrid = Columns(rowModesModel, setRowModesModel, rows);
    const slots = {
        loadingOverlay: () => <DataGridSkeleton columns={columnsForGrid} rowLength={25} />,
        noRowsOverlay: () => (
            <NoRowsOverlay hasActiveFilters={hasActiveFilters} downloadSampleFile={downloadSampleFile} />
        ),
    };

    const paginationClickHandler = newPaginationModel => {
        setPaginationModel(newPaginationModel);
        const { search, filter } = getCurrentSearchAndFilter();
        if (uploadId) {
            fetchUsersWithUploadId(uploadId, newPaginationModel.page + 1, newPaginationModel.pageSize, search, filter);
        }
    };

    const handlePaginationModelChange = newPaginationModel => {
        paginationClickHandler(newPaginationModel);
    };
    const initialState = {
        ...DATA_GRID_DEFAULTS.initialState,
        sorting: {
            sortModel: [{ field: 'firstName', sort: 'asc' }],
        },
    };

    const NonBulkActions = () => (
        <Button
            variant="contained"
            color="primary"
            size="medium"
            endIcon={<GroupAddIcon />}
            onClick={handleImportAllUsersClick}
            disabled={overallTotalItems === 0}>
            {formatMessage(Locale.importAllUsers)}
        </Button>
    );
    const BulkActions = () => (
        <Button
            variant="contained"
            color="primary"
            size="medium"
            endIcon={<AddIcon />}
            onClick={() => {
                handleImportAllUsersModalContinue(false, true);
            }}>
            {formatMessage(Locale.importSelectedUsers)}
        </Button>
    );

    const slotProps = {
        ...DATA_GRID_DEFAULTS.slotProps,
        filterPanel: {
            sx: Styled.filterPanelStyles,
        },
        toolbar: {
            ...DATA_GRID_DEFAULTS.slotProps.toolbar,
            NonBulkActions,
            BulkActions,
            defaultFilterPanel: true,
            onCancelSearch: searchProps.onCancelSearch,
            onSearchChange: searchProps.onSearchChange,
            searchText: searchProps.searchText,
            searchVisibleTooltip: false,
            selectedItems: selectedRows.length,
            searchPlaceholder: formatMessage(Locale.searchPlaceholder) || 'Search by name or email...',
            isSearchable: true,
            isFilterable: true,
            showQuickFilter: true,
        },
        pagination: {
            ...DATA_GRID_DEFAULTS.slotProps.pagination,
            labelDisplayedRows: paginationInfo =>
                `${paginationInfo.from} - ${paginationInfo.to} of ${paginationInfo.count} items`,
            showFirstButton: false,
            showLastButton: false,
        },
    };

    const handleProcessRowUpdate = async (newRow, oldRow) => {
        const shouldUpdate = Object.keys(newRow).every(key => newRow[key] === oldRow[key]);
        if (!shouldUpdate) {
            try {
                const editRowResult = await editUserRow({ newRow }).unwrap();
                const updatedRow = { ...editRowResult, isNew: false };
                setRows(rows.map(row => (row.id === newRow.id ? updatedRow : row)));
                return updatedRow;
            } catch (error) {
                dispatch(setIsUpdateUserFailed());
                return oldRow;
            }
        }
        return oldRow;
    };

    return (
        <>
            <DataGrid
                {...DATA_GRID_DEFAULTS}
                initialState={initialState}
                columns={columnsForGrid}
                loading={isGridLoading}
                checkboxSelection
                paginationMode="server"
                pagination={!isGridLoading}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowSelectionModelChange={newSelectionModel => {
                    setSelectedRows(newSelectionModel);
                }}
                density={density}
                onDensityChange={newDensity => setDensity(newDensity)}
                disableColumnMenu={true}
                disableColumnSorting={true}
                pageSizeOptions={[10, 25, 50]}
                rowCount={totalCount}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                rows={rows}
                slots={slots}
                processRowUpdate={handleProcessRowUpdate}
                onProcessRowUpdateError={error => {
                    console.error('Row update error:', error);
                }}
                filterModel={filterProps.filterModel}
                onFilterModelChange={filterProps.onFilterModelChange}
                disableMultipleColumnsFiltering
                slotProps={slotProps}
                sx={Styled.dataGridRootStyles}
            />
            {showImportAllUsersModal && (
                <ImportAllUsersModal
                    open={showImportAllUsersModal}
                    totalUsersCount={overallTotalItems}
                    pageSize={paginationModel.pageSize}
                    currentPageRecordsCount={rows?.length || 0}
                    handleClose={handleImportAllUsersModalClose}
                    handleContinue={handleImportAllUsersModalContinue}
                    isLoading={isCreatingUsers}
                />
            )}
            {isImportSuccess && (
                <ToastNotification message={importSuccessMessage} severity="success" onClose={handleImportSuccess} />
            )}
            {isImportError && (
                <ToastNotification message={importErrorMessage} severity="error" onClose={handleImportError} />
            )}
        </>
    );
};

export default BulkUserImportGrid;
